// Audio pronunciation utility using Web Speech API
class AudioPronunciation {
  private speechSynthesis: SpeechSynthesis;
  private utterance: SpeechSynthesisUtterance | null = null;
  private voicesLoaded: boolean = false;

  constructor() {
    this.speechSynthesis = window.speechSynthesis;
    this.initializeVoices();
  }

  // Initialize voices
  private initializeVoices(): void {
    // Some browsers need time to load voices
    if (this.speechSynthesis.getVoices().length > 0) {
      this.voicesLoaded = true;
    } else {
      this.speechSynthesis.onvoiceschanged = () => {
        this.voicesLoaded = true;
        console.log('Voices loaded:', this.speechSynthesis.getVoices().length);
      };
    }
  }

  // Get available voices
  getVoices(): SpeechSynthesisVoice[] {
    return this.speechSynthesis.getVoices();
  }

  // Get Persian/Farsi voices if available
  getPersianVoices(): SpeechSynthesisVoice[] {
    const voices = this.getVoices();
    const persianVoices = voices.filter(voice => 
      voice.lang.includes('fa') || 
      voice.lang.includes('ir') || 
      voice.lang.includes('persian') ||
      voice.name.toLowerCase().includes('persian') ||
      voice.name.toLowerCase().includes('farsi') ||
      voice.name.toLowerCase().includes('iran') ||
      voice.name.toLowerCase().includes('tehran') ||
      // More specific checks for known Persian voices
      voice.name.toLowerCase().includes('aria') ||
      voice.name.toLowerCase().includes('majid') ||
      voice.name.toLowerCase().includes('damavand') ||
      voice.name.toLowerCase().includes('roya')
    );
    
    console.log('Available Persian voices:', persianVoices.map(v => `${v.name} (${v.lang})`));
    return persianVoices;
  }

  // Get the best available voice for Persian
  getBestPersianVoice(): SpeechSynthesisVoice | null {
    const persianVoices = this.getPersianVoices();
    
    if (persianVoices.length > 0) {
      // Prioritize specific Persian voices if available
      const preferredVoices = ['aria', 'roya', 'damavand', 'majid'];
      for (const preferred of preferredVoices) {
        const voice = persianVoices.find(v => 
          v.name.toLowerCase().includes(preferred)
        );
        if (voice) {
          console.log('Using preferred Persian voice:', voice.name);
          return voice;
        }
      }
      
      // Return the first Persian voice found
      console.log('Using available Persian voice:', persianVoices[0].name);
      return persianVoices[0];
    }
    
    // Try to find any voice that might work for Persian
    const allVoices = this.getVoices();
    
    // First try Arabic voices (most likely to work well with Persian)
    const arabicVoices = allVoices.filter(voice => 
      voice.lang.includes('ar') && 
      !voice.lang.includes('ar-eg') // Avoid Egyptian Arabic
    );
    
    if (arabicVoices.length > 0) {
      // Prefer specific Arabic voices that work well with Persian
      const preferredArabicVoices = ['majed', 'tarik', 'omar'];
      for (const preferred of preferredArabicVoices) {
        const voice = arabicVoices.find(v => 
          v.name.toLowerCase().includes(preferred)
        );
        if (voice) {
          console.log('Using preferred Arabic voice for Persian:', voice.name);
          return voice;
        }
      }
      
      console.log('Using available Arabic voice for Persian:', arabicVoices[0].name);
      return arabicVoices[0];
    }
    
    // Then try other related languages
    const potentialVoices = allVoices.filter(voice => 
      voice.lang.includes('ur') || // Urdu
      voice.lang.includes('hi') || // Hindi
      voice.lang.includes('tr') || // Turkish
      voice.name.toLowerCase().includes('middle') ||
      voice.name.toLowerCase().includes('eastern')
    );
    
    if (potentialVoices.length > 0) {
      console.log('Using potential fallback voice for Persian:', potentialVoices[0].name);
      return potentialVoices[0];
    }
    
    // Last resort: use any available voice
    if (allVoices.length > 0) {
      console.log('Using any available voice as last resort:', allVoices[0].name);
      return allVoices[0];
    }
    
    return null;
  }

  // Speak text with Persian pronunciation
  speak(text: string, options: {
    rate?: number;
    pitch?: number;
    volume?: number;
    voice?: SpeechSynthesisVoice;
  } = {}): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        // Cancel any ongoing speech more gently
        if (this.utterance) {
          this.speechSynthesis.cancel();
          this.utterance = null;
          // Add a small delay to ensure cancellation is complete
          setTimeout(() => this.startSpeech(text, options, resolve, reject), 100);
        } else {
          this.startSpeech(text, options, resolve, reject);
        }
      } catch (error) {
        console.error('Error in speech synthesis:', error);
        reject(error);
      }
    });
  }

  private startSpeech(
    text: string, 
    options: { rate?: number; pitch?: number; volume?: number; voice?: SpeechSynthesisVoice },
    resolve: () => void,
    reject: (error: Error) => void
  ): void {
    try {
      // Check if speech synthesis is available
      if (!this.speechSynthesis) {
        reject(new Error('Speech synthesis not available'));
        return;
      }

      // Create new utterance
      this.utterance = new SpeechSynthesisUtterance(text);
      
      // Try to use the best available Persian voice
      const bestVoice = options.voice || this.getBestPersianVoice();
      if (bestVoice) {
        this.utterance.voice = bestVoice;
        // Set language based on the voice's language, with fallback to Persian
        this.utterance.lang = bestVoice.lang || 'fa-IR';
        console.log('Using voice for Persian text:', bestVoice.name, `(${bestVoice.lang})`);
      } else {
        console.log('No suitable voice found, using system default');
        // Try to set language to Persian even without a Persian voice
        this.utterance.lang = 'fa-IR';
      }
      
      // Set speech parameters - adjust based on voice type
      const isArabicVoice = bestVoice?.lang?.includes('ar');
      this.utterance.rate = options.rate || (isArabicVoice ? 0.6 : 0.5); // Slightly faster for Arabic voices
      this.utterance.pitch = options.pitch || 1.0;
      this.utterance.volume = options.volume || 1.0;

      // Track speech state
      let speechStarted = false;
      let speechCompleted = false;

      // Event handlers
      this.utterance.onstart = () => {
        speechStarted = true;
        console.log('Speech started for:', text);
      };

      this.utterance.onend = () => {
        if (!speechCompleted) {
          speechCompleted = true;
          console.log('Speech ended for:', text);
          this.utterance = null;
          resolve();
        }
      };

      this.utterance.onerror = (event) => {
        if (!speechCompleted) {
          speechCompleted = true;
          console.error('Speech synthesis error:', event);
          this.utterance = null;
          
          let errorMessage = 'Speech synthesis failed';
          if (event.error) {
            const errorCode = event.error as string;
            switch (errorCode) {
              case 'canceled':
                // If speech was canceled but had started, consider it successful
                if (speechStarted) {
                  console.log('Speech was canceled after starting, considering successful');
                  resolve();
                  return;
                }
                errorMessage = 'Speech was canceled';
                break;
              case 'interrupted':
                errorMessage = 'Speech was interrupted';
                break;
              case 'audio-busy':
                errorMessage = 'Audio system is busy';
                break;
              case 'audio-hardware':
                errorMessage = 'Audio hardware error';
                break;
              case 'network':
                errorMessage = 'Network error';
                break;
              case 'synthesis-failed':
                errorMessage = 'Speech synthesis failed';
                break;
              case 'text-too-long':
                errorMessage = 'Text is too long';
                break;
              case 'invalid-argument':
                errorMessage = 'Invalid argument';
                break;
              default:
                errorMessage = `Speech error: ${errorCode}`;
            }
          }
          
          reject(new Error(errorMessage));
        }
      };

      // Add timeout as safety measure
      const timeout = setTimeout(() => {
        if (!speechCompleted) {
          speechCompleted = true;
          console.warn('Speech synthesis timeout for:', text);
          this.stop();
          reject(new Error('Speech synthesis timeout'));
        }
      }, 10000); // 10 second timeout

      // Clear timeout when speech completes
      const originalOnEnd = this.utterance.onend;
      this.utterance.onend = (event) => {
        clearTimeout(timeout);
        if (originalOnEnd) originalOnEnd.call(this.utterance, event);
      };

      const originalOnError = this.utterance.onerror;
      this.utterance.onerror = (event) => {
        clearTimeout(timeout);
        if (originalOnError) originalOnError.call(this.utterance, event);
      };

      // Start speaking
      this.speechSynthesis.speak(this.utterance);

      // Double-check if speech started (some browsers need this)
      setTimeout(() => {
        if (!speechStarted && !speechCompleted && this.utterance) {
          console.warn('Speech may not have started, retrying...');
          this.speechSynthesis.cancel();
          this.speechSynthesis.speak(this.utterance);
        }
      }, 500);

    } catch (error) {
      reject(error);
    }
  }

  // Stop current speech
  stop(): void {
    if (this.utterance) {
      this.speechSynthesis.cancel();
      this.utterance = null;
    }
  }

  // Check if speech synthesis is supported
  isSupported(): boolean {
    return 'speechSynthesis' in window && 'SpeechSynthesisUtterance' in window;
  }

  // Check if speech synthesis is speaking
  isSpeaking(): boolean {
    return this.speechSynthesis.speaking;
  }

  // Wait for voices to load (needed for some browsers)
  async waitForVoices(): Promise<void> {
    return new Promise((resolve) => {
      if (this.voicesLoaded && this.speechSynthesis.getVoices().length > 0) {
        resolve();
      } else {
        let attempts = 0;
        const maxAttempts = 50; // 5 seconds max wait
        
        const checkVoices = () => {
          attempts++;
          if (this.speechSynthesis.getVoices().length > 0) {
            this.voicesLoaded = true;
            console.log('Voices loaded successfully after', attempts * 100, 'ms');
            resolve();
          } else if (attempts < maxAttempts) {
            setTimeout(checkVoices, 100);
          } else {
            console.warn('Voice loading timeout, proceeding without voices');
            this.voicesLoaded = true;
            resolve();
          }
        };
        checkVoices();
      }
    });
  }

  // Test audio functionality
  async testAudio(): Promise<boolean> {
    try {
      await this.speak('سلام', { rate: 0.8 });
      return true;
    } catch (error) {
      console.error('Audio test failed:', error);
      return false;
    }
  }
}

// Create singleton instance
const audioPronunciation = new AudioPronunciation();

// TTS functionality completely removed - only generated audio files supported
export const speakText = async (text: string, options?: any): Promise<void> => {
  console.log('🚫 TTS functionality removed - only generated audio files supported');
  console.log(`🚫 No audio file available for: ${text}`);
  return Promise.resolve();
};

// Audio playback function that only uses generated audio files
export const playSmartAudio = async (audioUrl?: string, text?: string, options?: any): Promise<void> => {
  try {
    // Import the audio checker dynamically to avoid circular dependencies
    const { checkAudioExists, isLikelyPlaceholder } = await import('./audioFileChecker');
    
    // Check if we have a valid audio URL
    if (!audioUrl) {
      if (text) {
        console.log(`🚫 No audio URL provided for: ${text}`);
      }
      console.warn('⚠️ No audio source provided');
      return;
    }

    // Check if it's likely a placeholder URL
    if (isLikelyPlaceholder(audioUrl)) {
      if (text) {
        console.log(`🚫 Placeholder audio URL detected for: ${text}`);
        console.log('🚫 Use generate_audio.sh to create audio files');
      }
      return;
    }

    // Check if the audio file actually exists
    console.log(`🔍 Checking audio file existence: ${audioUrl}`);
    const checkResult = await checkAudioExists(audioUrl);
    
    if (!checkResult.exists || !checkResult.isAudio) {
      if (text) {
        console.log(`🚫 Audio file does not exist or is invalid for: ${text}`);
        if (checkResult.error) {
          console.log(`🚫 Error: ${checkResult.error}`);
        }
        console.log('🚫 Use generate_audio.sh to create audio files');
      }
      return;
    }

    // Audio file exists and is valid - play it
    console.log(`🎵 Playing verified audio file: ${audioUrl}`);
    await playAudio(audioUrl, text);
    
  } catch (error) {
    console.error('❌ Error in playSmartAudio:', error);
    return Promise.resolve();
  }
};

export const stopSpeaking = (): void => {
  audioPronunciation.stop();
};

export const isAudioSupported = (): boolean => {
  return audioPronunciation.isSupported();
};

export const isSpeaking = (): boolean => {
  return audioPronunciation.isSpeaking();
};

export const getPersianVoices = (): SpeechSynthesisVoice[] => {
  return audioPronunciation.getPersianVoices();
};


export const playGanjiAdabiAudio = async (audioUrl: string): Promise<void> => {
  try {
    console.log(`🎵 Playing ganji-adabi audio: ${audioUrl}`);
    
    
    if (audioUrl.includes('/audio/unknown-pathway/unknown-lesson/audio.mp3')) {
      const fullUrl = audioUrl.startsWith('/') ? `${window.location.origin}${audioUrl}` : audioUrl;
      
      const response = await fetch(fullUrl);
      if (!response.ok) {
        console.log(`🚫 HTTP ${response.status}: ${response.statusText}`);
        return;
      }
      
      const contentType = response.headers.get('content-type');
      
      // Check if response is JSON (containing the actual audio URL)
      if (contentType && contentType.includes('application/json')) {
        const data = await response.json();
        if (data.success && data.audioUrl) {
          // Use the returned audio URL
          const audio = new Audio(data.audioUrl);
          await audio.play();
          console.log('✅ Ganji-adabi audio played successfully');
          return;
        } else if (data.fallback) {
          console.log('🚫 Server requested TTS fallback - TTS functionality removed');
          return;
        } else {
          console.log('🚫 Failed to get audio URL:', data.message || 'Unknown error');
          return;
        }
      } else if (contentType && contentType.includes('audio/')) {
        // Response is direct audio file
        const audio = new Audio(fullUrl);
        await audio.play();
        console.log('✅ Ganji-adabi audio played successfully');
        return;
      } else {
        console.log('🚫 Unexpected response type');
        return;
      }
    } else {
      // Direct audio URL
      const audio = new Audio(audioUrl);
      await audio.play();
      console.log('✅ Audio played successfully');
    }
  } catch (error) {
    console.error('❌ Error playing ganji-adabi audio:', error);
    throw error;
  }
};


export const playAudio = async (audioUrl?: string, text?: string): Promise<void> => {
  try {
    
    if (audioUrl && audioUrl.includes('ganji-adabi')) {
      await playGanjiAdabiAudio(audioUrl);
      return;
    }
    
    // If we have text but no audio URL, log and return
    if (text && !audioUrl) {
      console.warn('⚠️ No audio URL provided for text:', text);
      console.log('🚫 Use generate_audio.sh to create audio files');
      return;
    }
    
    // If we have a regular audio URL, play it
    if (audioUrl) {
      const audio = new Audio(audioUrl);
      await audio.play();
      return;
    }
    
    console.warn('⚠️ No audio source provided');
  } catch (error) {
    console.error('❌ Error playing audio:', error);
    
    // No TTS fallback - just log and return
    if (text) {
      console.error('❌ Audio playback failed for:', text);
      console.log('🚫 Use generate_audio.sh to create audio files');
    }
    return;
  }
};

// Enhanced debug function to log audio system status
export const debugAudioSystem = (): void => {
  console.log('=== Audio System Debug ===');
  console.log('Speech synthesis supported:', 'speechSynthesis' in window);
  console.log('SpeechSynthesisUtterance supported:', 'SpeechSynthesisUtterance' in window);
  console.log('Total voices available:', audioPronunciation.getVoices().length);
  console.log('Persian voices available:', audioPronunciation.getPersianVoices().length);
  console.log('Currently speaking:', audioPronunciation.isSpeaking());
  
  const allVoices = audioPronunciation.getVoices();
  const persianVoices = audioPronunciation.getPersianVoices();
  const bestVoice = audioPronunciation.getBestPersianVoice();
  
  console.log('All voices:', allVoices.map(v => `${v.name} (${v.lang}) - ${v.localService ? 'Local' : 'Remote'}`));
  console.log('Persian voices:', persianVoices.map(v => `${v.name} (${v.lang}) - ${v.localService ? 'Local' : 'Remote'}`));
  console.log('Best Persian voice:', bestVoice ? `${bestVoice.name} (${bestVoice.lang})` : 'None found');
  
  // Group voices by language for better analysis
  const voicesByLang = allVoices.reduce((acc, voice) => {
    const lang = voice.lang.split('-')[0]; // Get main language code
    if (!acc[lang]) acc[lang] = [];
    acc[lang].push(voice.name);
    return acc;
  }, {} as Record<string, string[]>);
  
  console.log('Voices grouped by language:', voicesByLang);
  console.log('========================');
};

// TTS testing removed - only audio files supported
export const testSpeechSynthesis = async (testText: string = 'سلام'): Promise<boolean> => {
  console.log('🧪 TTS testing removed - only audio files supported');
  return false;
};

// Force reload voices (useful for debugging)
export const reloadVoices = async (): Promise<void> => {
  console.log('🔄 Reloading voices...');
  if ('speechSynthesis' in window) {
    window.speechSynthesis.cancel();
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Trigger voice reload
    window.speechSynthesis.getVoices();
    await audioPronunciation.waitForVoices();
    
    console.log('✅ Voices reloaded');
    debugAudioSystem();
  }
};

export default audioPronunciation; 